export interface StockDocumentMutationOptions {
  idempotencyKey?: string;
  expectedUpdatedAt?: string | null;
  keepalive?: boolean;
}

export type StockMutationRequestOptions = {
  idempotencyKey?: string;
  headers?: Record<string, string>;
  keepalive?: boolean;
};

export interface StockDocumentMutationDeps<Entity extends string> {
  rememberStockDocumentUpdatedAt: (
    entity: Entity,
    id: string | null | undefined,
    updatedAt: string | null | undefined,
  ) => void;
  forgetStockDocumentUpdatedAt: (entity: Entity, id: string | null | undefined) => void;
  withExpectedUpdatedAt: (
    entity: Entity,
    id: string,
    options?: StockDocumentMutationOptions,
  ) => StockMutationRequestOptions;
}

function stockDocumentCacheKey(entity: string, id: string): string {
  return `${entity}:${id}`;
}

export function createStockDocumentMutationCache<
  Entity extends string,
>(): StockDocumentMutationDeps<Entity> {
  const stockDocumentUpdatedAtCache = new Map<string, string>();

  function rememberStockDocumentUpdatedAt(
    entity: Entity,
    id: string | null | undefined,
    updatedAt: string | null | undefined,
  ): void {
    const normalizedId = (id ?? '').trim();
    const normalizedUpdatedAt = (updatedAt ?? '').trim();
    if (!normalizedId || !normalizedUpdatedAt) {
      return;
    }
    stockDocumentUpdatedAtCache.set(
      stockDocumentCacheKey(entity, normalizedId),
      normalizedUpdatedAt,
    );
  }

  function forgetStockDocumentUpdatedAt(entity: Entity, id: string | null | undefined): void {
    const normalizedId = (id ?? '').trim();
    if (!normalizedId) {
      return;
    }
    stockDocumentUpdatedAtCache.delete(stockDocumentCacheKey(entity, normalizedId));
  }

  function withExpectedUpdatedAt(
    entity: Entity,
    id: string,
    options?: StockDocumentMutationOptions,
  ): StockMutationRequestOptions {
    const requestOptions: StockMutationRequestOptions = {};
    if (options?.idempotencyKey) {
      requestOptions.idempotencyKey = options.idempotencyKey;
    }
    if (options?.keepalive) {
      requestOptions.keepalive = true;
    }
    const expectedUpdatedAt =
      options?.expectedUpdatedAt?.trim() ||
      stockDocumentUpdatedAtCache.get(stockDocumentCacheKey(entity, id));
    if (!expectedUpdatedAt) {
      return requestOptions;
    }
    requestOptions.headers = {
      'X-Expected-Updated-At': expectedUpdatedAt,
    };
    return requestOptions;
  }

  return {
    rememberStockDocumentUpdatedAt,
    forgetStockDocumentUpdatedAt,
    withExpectedUpdatedAt,
  };
}

type RememberStockDocumentUpdatedAt<Entity extends string> =
  StockDocumentMutationDeps<Entity>['rememberStockDocumentUpdatedAt'];

export function rememberResponseUpdatedAt<Entity extends string, Response>(
  rememberStockDocumentUpdatedAt: RememberStockDocumentUpdatedAt<Entity>,
  entity: Entity,
  response: Response,
  getId: (response: Response) => string | null | undefined,
  getUpdatedAt: (response: Response) => string | null | undefined,
): Response {
  rememberStockDocumentUpdatedAt(entity, getId(response), getUpdatedAt(response));
  return response;
}

export function rememberResponseItemsUpdatedAt<Entity extends string, Response, Item>(
  rememberStockDocumentUpdatedAt: RememberStockDocumentUpdatedAt<Entity>,
  entity: Entity,
  response: Response,
  getItems: (response: Response) => readonly Item[],
  getId: (item: Item) => string | null | undefined,
  getUpdatedAt: (item: Item) => string | null | undefined,
): Response {
  for (const item of getItems(response)) {
    rememberStockDocumentUpdatedAt(entity, getId(item), getUpdatedAt(item));
  }
  return response;
}
